/**
 * Mutual exclusion the engine and the capture path REQUIRE; the host supplies
 * HOW. The engine decides WHEN a lock is needed (that is ordering, and ordering
 * lives in the engine); a platform holder decides how one is taken —
 * `navigator.locks` in a browser, a pass-through in a single-process runtime
 * where no sibling can contend.
 */
export interface LockManager {
  withExclusiveLock<T>(name: string, fn: () => Promise<T>): Promise<T>;
  /** Runs `fn` only if the lock is free RIGHT NOW; otherwise reports it held. */
  tryWithExclusiveLock<T>(
    name: string,
    fn: () => Promise<T>,
  ): Promise<{ acquired: true; value: T } | { acquired: false }>;
}

export function syncLockName(profileId: string): string {
  return `njbsoft-fieldwork-sync:${profileId}`;
}

export function draftLockName(assignmentId: string): string {
  return `njbsoft-fieldwork-draft:${assignmentId}`;
}

/** The Web Locks options the two mappings below use. */
export interface WebLockRequestOptions {
  mode: "exclusive";
  ifAvailable?: boolean;
}

/**
 * The slice of the Web Locks API this package consumes, declared here rather
 * than taken from a DOM lib: the package compiles against a node lib so the
 * browser global is not in scope, and a narrow shape is what a test double has
 * to fake. Under `ifAvailable`, the callback receives null when the lock is
 * already held.
 */
export interface WebLocksLike {
  request<T>(
    name: string,
    options: WebLockRequestOptions,
    fn: (lock: unknown) => Promise<T>,
  ): Promise<T>;
}

/**
 * A pass-through manager: correct for a runtime with one JS context and no
 * sibling holder that could contend, where every caller is already serialized
 * by the single event loop. `tryWithExclusiveLock` therefore always acquires.
 */
export function createSingleProcessLockManager(): LockManager {
  return {
    async withExclusiveLock(_name, fn) {
      return await fn();
    },
    async tryWithExclusiveLock(_name, fn) {
      return { acquired: true, value: await fn() };
    },
  };
}

/** Maps the contract onto a Web Locks holder (a browser's `navigator.locks`). */
export function createLockManagerFromNavigatorLocks(locks: WebLocksLike): LockManager {
  return {
    async withExclusiveLock(name, fn) {
      return await locks.request(name, { mode: "exclusive" }, () => fn());
    },
    async tryWithExclusiveLock(name, fn) {
      return await locks.request(name, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        // A null lock is the Web Locks API reporting the name already held.
        if (lock === null) return { acquired: false as const };
        return { acquired: true as const, value: await fn() };
      });
    },
  };
}
